import * as React from 'react';

import { Input as BaseInput } from '@base-ui/react/input';

import { cn } from '@/lib/cn';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  /** Error state - shows red border */
  error?: boolean;
  /** Visual variant */
  variant?: 'default' | 'soft';
  /** Size variant for styling (not HTML size attribute) */
  inputSize?: 'default' | 'sm';
}

/**
 * Input component built on Base UI for accessible behavior.
 *
 * Note: Per ui-skills, NEVER block paste in input elements.
 * This component does not interfere with paste functionality.
 *
 * @example
 * // Basic input
 * <Input placeholder="Enter your name" />
 *
 * @example
 * // With error state
 * <Input error placeholder="Email" />
 *
 * @example
 * // Disabled
 * <Input disabled placeholder="Cannot edit" />
 */
const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type = 'text', error, variant = 'default', inputSize = 'default', style, ...props }, ref) => {
    return (
      <BaseInput
        ref={ref}
        type={type}
        {...(style !== undefined ? { style } : {})}
        className={cn(
          // Base styles - industrial/utilitarian
          'flex w-full rounded-md bg-background text-sm',
          // Size variants
          inputSize === 'default' && 'h-10 px-3 py-2',
          inputSize === 'sm' && 'h-7 px-2 py-1',
          // Text styling
          'text-foreground placeholder:text-muted-foreground',
          // Variant-specific styling
          variant === 'default' && [
            'border border-border',
            // Inset: an outside ring is cut by any scroll or overflow parent (seller
            // scanner field at the screen edge, 03.11.2026).
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
            'hover:border-ring/50',
          ],
          variant === 'soft' && [
            'border-0 bg-surface-muted ring-1 ring-info-border/80',
            'shadow-control',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-info/70',
            'hover:shadow-control hover:ring-info-border',
          ],
          // Disabled state
          'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-border',
          // Error state - variant-specific overrides
          error && variant === 'default' && 'border-destructive focus-visible:ring-destructive',
          error && variant === 'soft' && 'ring-destructive/60 focus-visible:ring-destructive',
          // File input styling
          'file:border-0 file:bg-transparent file:text-sm file:font-medium',
          // Transition
          ' duration-150',
          className,
        )}
        {...props}
      />
    );
  },
);

Input.displayName = 'Input';

export { Input };
